/**
 * Users Management Page
 */

import { useEffect, useState } from 'react';
import { apiClient } from '../api/client';
import type { User, CreateUserInput, Tenant } from '../api/types';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Modal, ConfirmModal } from '../components/Modal';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeaderCell,
  TableCell,
  EmptyState,
} from '../components/Table';
import { Plus, Trash2, Edit2, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';

export function UsersPage() {
  const [isLoading, setIsLoading] = useState(true);
  const [users, setUsers] = useState<User[]>([]);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setIsLoading(true);
    try {
      const res = await apiClient.listUsers();
      setUsers(res.users);
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateUser = async (input: CreateUserInput) => {
    try {
      await apiClient.createUser(input);
      setIsCreateOpen(false);
      loadUsers();
    } catch (err) {
      console.error('Failed to create user:', err);
      throw err;
    }
  };

  const handleUpdateUser = async (input: { email?: string; name?: string; org?: string | null }) => {
    if (!selectedUser) return;
    try {
      await apiClient.updateUser(selectedUser.uuid, input);
      setIsEditOpen(false);
      setSelectedUser(null);
      loadUsers();
    } catch (err) {
      console.error('Failed to update user:', err);
      throw err;
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    try {
      await apiClient.deleteUser(selectedUser.uuid);
      setIsDeleteOpen(false);
      setSelectedUser(null);
      loadUsers();
    } catch (err) {
      console.error('Failed to delete user:', err);
    }
  };

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Users</h1>
          <p className="text-gray-500 mt-1">
            Manage users who can be assigned API keys
          </p>
        </div>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={loadUsers}>
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
          <Button onClick={() => setIsCreateOpen(true)}>
            <Plus className="w-4 h-4" />
            Add User
          </Button>
        </div>
      </div>

      {/* Users Table */}
      <Card>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell>Name</TableHeaderCell>
              <TableHeaderCell>Email</TableHeaderCell>
              <TableHeaderCell>Organization</TableHeaderCell>
              <TableHeaderCell>Tenant</TableHeaderCell>
              <TableHeaderCell>Created</TableHeaderCell>
              <TableHeaderCell align="right">Actions</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center">
                  Loading...
                </td>
              </tr>
            ) : users.length === 0 ? (
              <EmptyState message="No users found. Add a user to get started." colSpan={6} />
            ) : (
              users.map((user) => (
                <TableRow key={user.uuid}>
                  <TableCell className="font-medium">{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.org || '-'}</TableCell>
                  <TableCell>
                    <code className="text-sm bg-gray-100 px-2 py-0.5 rounded">
                      {user.tenantId}
                    </code>
                  </TableCell>
                  <TableCell>
                    {format(new Date(user.createdAt), 'MMM d, yyyy')}
                  </TableCell>
                  <TableCell align="right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedUser(user);
                          setIsEditOpen(true);
                        }}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedUser(user);
                          setIsDeleteOpen(true);
                        }}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Create User Modal */}
      <CreateUserModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onCreate={handleCreateUser}
      />

      {/* Edit User Modal */}
      {selectedUser && (
        <EditUserModal
          isOpen={isEditOpen}
          onClose={() => {
            setIsEditOpen(false);
            setSelectedUser(null);
          }}
          user={selectedUser}
          onSave={handleUpdateUser}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={isDeleteOpen}
        onClose={() => {
          setIsDeleteOpen(false);
          setSelectedUser(null);
        }}
        onConfirm={handleDeleteUser}
        title="Delete User"
        message={`Are you sure you want to delete "${selectedUser?.name}"? This will not affect existing API keys.`}
        confirmText="Delete User"
        variant="danger"
      />
    </div>
  );
}

// Create User Form Modal
function CreateUserModal({
  isOpen,
  onClose,
  onCreate,
}: {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (input: CreateUserInput) => Promise<void>;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [form, setForm] = useState<CreateUserInput>({
    email: '',
    name: '',
    org: '',
    tenantId: '',
  });

  // Load tenants when modal opens
  useEffect(() => {
    if (isOpen) {
      setTenantsLoading(true);
      apiClient
        .listTenants()
        .then((res) => {
          setTenants(res.tenants);
          // Auto-select first tenant if available and none selected
          if (res.tenants.length > 0 && !form.tenantId) {
            setForm((f) => ({ ...f, tenantId: res.tenants[0].slug }));
          }
        })
        .catch((err) => console.error('Failed to load tenants:', err))
        .finally(() => setTenantsLoading(false));
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      await onCreate(form);
      setForm({
        email: '',
        name: '',
        org: '',
        tenantId: tenants.length > 0 ? tenants[0].slug : '',
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create user');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add User" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Tenant Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tenant
          </label>
          <select
            value={form.tenantId}
            onChange={(e) => setForm({ ...form, tenantId: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            disabled={tenantsLoading}
            required
          >
            {tenantsLoading ? (
              <option value="">Loading tenants...</option>
            ) : tenants.length === 0 ? (
              <option value="">No tenants available</option>
            ) : (
              tenants.map((tenant) => (
                <option key={tenant.uuid} value={tenant.slug}>
                  {tenant.name} ({tenant.slug})
                </option>
              ))
            )}
          </select>
          {tenants.length === 0 && !tenantsLoading && (
            <p className="mt-1 text-sm text-gray-500">
              <a href="/tenants" className="text-blue-600 hover:underline">Add a tenant</a> first before creating users.
            </p>
          )}
        </div>

        <Input
          label="Name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="e.g., John Doe"
          required
        />
        <Input
          label="Email"
          type="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="e.g., john@example.com"
          required
        />
        <Input
          label="Organization (optional)"
          value={form.org || ''}
          onChange={(e) => setForm({ ...form, org: e.target.value })}
          placeholder="e.g., Acme Inc"
        />

        {error && (
          <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isLoading} disabled={tenants.length === 0}>
            Add User
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// Edit User Form Modal
function EditUserModal({
  isOpen,
  onClose,
  user,
  onSave,
}: {
  isOpen: boolean;
  onClose: () => void;
  user: User;
  onSave: (input: { email?: string; name?: string; org?: string | null }) => Promise<void>;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    email: user.email,
    name: user.name,
    org: user.org || '',
  });

  // Reset form when user changes
  useEffect(() => {
    setForm({
      email: user.email,
      name: user.name,
      org: user.org || '',
    });
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      await onSave({
        email: form.email !== user.email ? form.email : undefined,
        name: form.name !== user.name ? form.name : undefined,
        org: form.org !== (user.org || '') ? (form.org || null) : undefined,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit User" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="e.g., John Doe"
          required
        />
        <Input
          label="Email"
          type="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="e.g., john@example.com"
          required
        />
        <Input
          label="Organization (optional)"
          value={form.org}
          onChange={(e) => setForm({ ...form, org: e.target.value })}
          placeholder="e.g., Acme Inc"
        />

        {error && (
          <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isLoading}>
            Save Changes
          </Button>
        </div>
      </form>
    </Modal>
  );
}
